import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { isReleaseGroups } from '../../../../shared/projectsV2';
import { userHasPermissionToCreateReleaseGroups, userHasPermissionToEditAnyReleaseGroup } from '../../../helpers/permissions';
import * as ProjectsV2Store from '../../../redux/projectsV2';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import NavigationButton from '../../generic/Button/NavigationButton';
import { icons } from '../../generic/Icon';
import PaginationRangeWithControl from '../../generic/Pagination/PaginationRangeWithControl';

import useFetchProjectsAction from './hooks/useFetchProjectsAction';
import { useReleaseGroupLimit } from './hooks/useReleaseGroupLimit';
import useShowReleaseGroupLimitModal from './hooks/useShowReleaseGroupLimitModal';
import ProjectActionsMenu from './ProjectActionsMenu';
import ProjectsSearch from './ProjectsSearch';

type CreateReleaseGroupButtonProps = {
  shouldRestrictCreateButton: boolean;
};

const CreateReleaseGroupButton: VoidFunctionComponent<CreateReleaseGroupButtonProps> = ({
  shouldRestrictCreateButton,
}) => {
  const showReleaseGroupLimitModal = useShowReleaseGroupLimitModal();
  if (shouldRestrictCreateButton) {
    return (
      <Button onClick={showReleaseGroupLimitModal} type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.LARGE} icon={icons.add}>
        New Release Group
      </Button>
    );
  }

  return (
    <NavigationButton to="/projects/group/create" type={BUTTON_TYPE.PRIMARY} size={BUTTON_SIZE.LARGE} icon={icons.add}>
      New Release Group
    </NavigationButton>
  );
};

const ProjectsToolbar: VoidFunctionComponent = () => {
  const page = useSelector(ProjectsV2Store.selectors.getPage);
  const count = useSelector(ProjectsV2Store.selectors.getCount);
  const totalProjects = useSelector(ProjectsV2Store.selectors.getTotalProjects);
  const category = useSelector(ProjectsV2Store.selectors.getCategory);
  const setPage = useFetchProjectsAction(ProjectsV2Store.actions.setPage);
  //  Could be null if totalProjects hasn't been fetched yet.

  const { isAtReleaseGroupLimit, isOverReleaseGroupLimit } = useReleaseGroupLimit();

  const canEditAnyReleaseGroups = userHasPermissionToEditAnyReleaseGroup();
  const canCreateReleaseGroups = userHasPermissionToCreateReleaseGroups();

  return (
    <div className="project-toolbar">
      <ProjectActionsMenu shouldRestrictCreateReleaseGroup={isAtReleaseGroupLimit || isOverReleaseGroupLimit} />
      <ProjectsSearch isReleaseGroups={isReleaseGroups(category)} />
      {isReleaseGroups(category) &&
        (canEditAnyReleaseGroups || canCreateReleaseGroups) && (
          <CreateReleaseGroupButton shouldRestrictCreateButton={isAtReleaseGroupLimit || isOverReleaseGroupLimit} />
        )}
      <PaginationRangeWithControl page={page} count={count} totalItems={totalProjects} setPage={setPage} />
    </div>
  );
};

export default ProjectsToolbar;
